<template>
  <div v-if="suggestedFilters && suggestedFilters.length > 0" class="suggested-filters mb-6">
    <div class="flex items-center gap-2 mb-3">
      <h3 class="text-base font-medium text-foreground">Suggested Filters</h3>
      <div class="text-xs text-muted-foreground">(Click to expand)</div>
    </div>
    <div class="overflow-x-auto -mx-4 px-4">
      <div class="flex gap-2 flex-nowrap min-w-min">
        <button 
          v-for="filterName in displayedFilters" 
          :key="filterName"
          @click="expandFilterSection(filterName)"
          class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm rounded-lg transition-all duration-200 shadow-xs"
          :class="isFilterSelected(filterName) 
            ? 'bg-primary text-primary-foreground shadow-primary/20' 
            : 'bg-card hover:bg-secondary/80 text-foreground border border-border'"
        >
          <SparklesIcon class="h-4 w-4" :class="isFilterSelected(filterName) ? 'text-primary-foreground' : 'text-primary'" />
          <span>{{ getFilterDisplayName(filterName) }}</span>
        </button>
      </div>
      <div v-if="suggestedFilters && suggestedFilters.length > 2" class="mt-2 text-center">
        <button 
          @click="showAll = !showAll"
          class="text-xs text-primary hover:underline"
        >
          {{ showAll ? 'Show less' : `Show ${suggestedFilters.length - 2} more` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { SparklesIcon } from '@heroicons/vue/24/solid'
import { useVehiclesStore } from '~/stores/vehicles'
import { useRouter } from '#imports'

const vehiclesStore = useVehiclesStore()
const router = useRouter()
const showAll = ref(false)

const emit = defineEmits<{
  'expand-and-scroll-to-filter': [filterName: string];
}>()

// Default suggested filters (keys from availableFilters)
const defaultSuggestions = ['make', 'model', 'body', 'priceRange', 'kmsRange', 'yearRange'];

// Suggested filters based on availableFilters keys
const suggestedFilters = computed(() => {
  const availFilters = vehiclesStore.availableFilters; // Get reference
  // Ensure availFilters is defined and is an object before using 'in'
  if (!availFilters || typeof availFilters !== 'object') {
      return []; // Return empty if filters aren't ready
  }
  
  // Filter default suggestions based on what actually exists in availableFilters
  return defaultSuggestions.filter(filterName => { 
     // Check if the key exists
     const keyExists = filterName in availFilters;
     if (!keyExists) return false;

     // Get the value safely
     const filterValue = availFilters[filterName as keyof typeof availFilters];

     // Add checks for non-empty options/valid ranges if needed
     return (
         (Array.isArray(filterValue) && filterValue.length > 0) ||
         (typeof filterValue === 'object' && filterValue !== null) // Simple check for objects (ranges)
     );
  });
});

const displayedFilters = computed(() => {
  const filters = suggestedFilters.value || [];
  return showAll.value ? filters : filters.slice(0, 4); // Show 4 by default
});

// Get display name (simple capitalization)
const getFilterDisplayName = (filterName: string): string => {
   // Convert camelCase or snake_case to Title Case
   const words = filterName.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').split(' ');
   return words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

// Check if a filter is currently selected/active
const isFilterSelected = (filterName: string): boolean => {
  const activeValue = vehiclesStore.activeFilters[filterName];
  if (activeValue === undefined || activeValue === null) return false;
  
  // Check based on type
  if (Array.isArray(activeValue)) return activeValue.length > 0;
  if (typeof activeValue === 'object' && 'min' in activeValue && 'max' in activeValue) {
      // Compare against defaults in availableFilters
      const defaultRange = vehiclesStore.availableFilters[filterName as keyof typeof vehiclesStore.availableFilters];
      if (defaultRange && typeof defaultRange === 'object' && 'min' in defaultRange && 'max' in defaultRange) {
           return activeValue.min !== defaultRange.min || activeValue.max !== defaultRange.max;
      }
      return true; // Consider active if defaults aren't available for comparison
  }
  return String(activeValue).trim() !== ''; // Check for non-empty string
};

// Find and expand the section containing the filter (Placeholder - needs integration with VehicleFilters)
const expandFilterSection = (filterName: string) => {
   console.warn(`[SuggestedFilters] expandFilterSection called for ${filterName} - Needs implementation to interact with VehicleFilters sections.`);
  // This logic needs to communicate with the parent or VehicleFilters component
  // to actually expand the relevant section, as UI schema is gone.
  // Option 1: Emit an event
  // emit('expand-filter', filterName); 
  emit('expand-and-scroll-to-filter', filterName);
  // Option 2: Use a shared state/composable if VehicleFilters is complex
};

// Remove resize handlers if not strictly needed anymore
// onMounted(() => { ... });
// onBeforeUnmount(() => { ... });

</script>

<style scoped>
.filter-highlight {
  animation: highlight-pulse 2s ease-in-out;
}

@keyframes highlight-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--primary), 0.7);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(var(--primary), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--primary), 0);
  }
}
</style>
